import React from 'react';
import styled from 'styled-components';

import MainButton from '@components/elements/MainButton';

import StyledTextBlocks from '@styled-components/StyledTextBlocks';

import {
	SubheadingH2,
	SmallSubheadingH2,
	SectionOverline,
} from '@styled-components/Typography';

const SectionWrapper = styled.section`
	position: relative;
	background: ${({ backgroundColor }) => backgroundColor || 'transparent'};
	overflow: ${({ showOverflow }) => (showOverflow ? 'visible' : 'hidden')};
`;

const BackgroundImage = styled.div`
	position: absolute;
	inset: 0;
	background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
	background-size: cover;
	background-position: center;
	opacity: 0;
	z-index: 0;
	animation: ${({ backgroundImage }) =>
		backgroundImage ? 'fadeIn 1s ease-in-out forwards' : 'none'};

	@keyframes fadeIn {
		to {
			opacity: 1;
		}
	}
`;

const SectionWrapperInner = styled.div`
	position: relative;
	z-index: 1;
	width: ${({ nestedSection }) => (nestedSection ? '100%' : '90%')};
	margin: 0 auto;
	max-width: ${({ maxWidth }) => maxWidth || 1320}px;
	padding-top: ${({ noTopPadding, theme, sectionHero }) => {
		if (noTopPadding) return '0px';
		if (sectionHero) return `calc(${theme.fixedHeights.navBar}px + 50px)`;
		return '50px';
	}};

	padding-bottom: ${({ noBottomPadding }) =>
		noBottomPadding ? '0px' : '40px'};
	padding-left: 0;
	padding-right: 0;

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		padding-top: ${({ noTopPadding, theme, sectionHero }) => {
			if (noTopPadding) return '0px';
			if (sectionHero) return `calc(${theme.fixedHeights.navBar}px + 144px)`;
			return '144px';
		}};
		padding-bottom: ${({ noBottomPadding }) =>
			noBottomPadding ? '0px' : '144px'};
	}
`;

const SectionHeader = styled.header`
	text-align: ${({ leftAlignHeader }) => (leftAlignHeader ? 'left' : 'center')};
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: ${({ leftAlignHeader }) =>
		leftAlignHeader ? 'start' : 'center'};
	gap: 8px;
	margin-bottom: ${({ noHeaderPadding }) => !noHeaderPadding && '5rem'};

	h2,
	.overline,
	.subhead {
		text-wrap: balance;
	}

	h2,
	.subhead {
		color: ${props =>
			props.darkBackgroundColor
				? props.theme.colors.white.main
				: props.theme.colors.black.main};
	}

	.overline {
		margin: 0 auto;
		margin-bottom: 1.5rem; /* 24px */
	}

	${({ theme }) => theme.mediaQuery.breakpointLg} {
		.section__subhead {
			max-width: ${({ maxHeaderWidth }) =>
				maxHeaderWidth ? maxHeaderWidth / 2 : 50}vw;
		}

		h2 {
			max-width: ${({ maxHeaderWidth }) => maxHeaderWidth || 100}vw;
		}
	}
`;

const SectionDescription = styled(StyledTextBlocks)`
	color: ${({ theme, darkBackgroundColor }) =>
		darkBackgroundColor ? theme.colors.white.main : theme.colors.black.main};
	font-family: ${({ theme }) => theme.fonts.body};
	font-size: ${({ subheadSize }) => (subheadSize ? subheadSize : 2)}rem;
	max-width: ${({ maxHeaderWidth }) => maxHeaderWidth}vw;

	${({ theme, maxHeaderWidth }) => `
		${theme.mediaQuery.breakpointLg} {
			max-width: ${maxHeaderWidth ? maxHeaderWidth / 2 : 30}vw;
		}
	`}

	span, p {
		color: ${({ theme, darkBackgroundColor }) =>
			darkBackgroundColor ? theme.colors.white.main : theme.colors.black.main};
	}
	a {
		color: ${({ theme, darkBackgroundColor }) =>
			darkBackgroundColor
				? theme.colors.white.main
				: theme.colors.primary.main};
		text-decoration: ${({ darkBackgroundColor }) =>
			darkBackgroundColor && 'underline'};
		&:hover {
			color: ${({ theme, darkBackgroundColor }) =>
				darkBackgroundColor
					? theme.colors.white.main
					: theme.colors.primary.dark};
			text-decoration: ${({ darkBackgroundColor }) =>
				darkBackgroundColor && 'none'};
		}
	}
`;

const BottomButtonWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	margin-top: 2rem;
`;

const Section = ({
	leftAlignHeader,
	overline,
	header,
	subheader,
	children,
	className,
	maxWidth,
	maxHeaderWidth,
	backgroundColor = 'transparent',
	backgroundImage,
	darkBackgroundColor,
	noTopPadding = false,
	noBottomPadding = false,
	nestedSection = false,
	noHeaderPadding,
	bodyTextSize,
	sectionHero,
	smallheader,
	bottomButton,
	headerCta,
	showOverflow,
	id,
}) => {
	return (
		<SectionWrapper
			backgroundColor={backgroundColor}
			id={id}
			showOverflow={showOverflow}
		>
			{backgroundImage && <BackgroundImage backgroundImage={backgroundImage} />}
			<SectionWrapperInner
				className={className}
				noTopPadding={noTopPadding}
				noBottomPadding={noBottomPadding}
				maxWidth={maxWidth}
				nestedSection={nestedSection}
				sectionHero={sectionHero}
			>
				{header && (
					<SectionHeader
						leftAlignHeader={leftAlignHeader}
						maxHeaderWidth={maxHeaderWidth}
						className="section-header"
						darkBackgroundColor={darkBackgroundColor}
						noHeaderPadding={noHeaderPadding}
					>
						{overline && (
							<SectionOverline className="overline">{overline}</SectionOverline>
						)}
						{smallheader ? (
							<SmallSubheadingH2 as="h2">{header}</SmallSubheadingH2>
						) : (
							<SubheadingH2> {header}</SubheadingH2>
						)}
						{subheader && (
							<SectionDescription
								className="section__subhead"
								textBlock={subheader}
								options={{ bodySize: bodyTextSize || 18 }}
								darkBackgroundColor={darkBackgroundColor}
							/>
						)}
						{headerCta && (
							<BottomButtonWrapper>
								<MainButton linkUrl={headerCta.linkUrl}>
									{headerCta.linkText}
								</MainButton>
							</BottomButtonWrapper>
						)}
					</SectionHeader>
				)}
				{children}
				{bottomButton && (
					<BottomButtonWrapper>
						<MainButton linkUrl={bottomButton.linkUrl}>
							{bottomButton.linkText}
						</MainButton>
					</BottomButtonWrapper>
				)}
			</SectionWrapperInner>
		</SectionWrapper>
	);
};

export default Section;
